import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PalantirButtonComponent, PalantirIconComponent } from '@flux/design-system';
import {
  DelegationFragmentFragment as DelegationFragment,
  DelegationScope,
} from '@generated/graphql';
import { FeatureFlagsEnum } from '@generated/featureFlags.enum';
import { AuthService } from '../../../core/services/auth/auth.service';
import { DelegationContextService } from '../../../core/services/delegation-context.service';
import { FeatureFlagService } from '../../../core/services/feature-flag/feature-flag.service';
import { DelegationsService } from '../../../features/delegations/services/delegations.service';

const SCOPE_LABELS: Record<DelegationScope, string> = {
  [DelegationScope.All]: 'Todos',
  [DelegationScope.Cards]: 'Cards',
  [DelegationScope.Tasks]: 'Tarefas',
  [DelegationScope.Reviews]: 'Revisões',
  [DelegationScope.Solicitations]: 'Solicitações',
};

@Component({
  selector: 'app-delegation-indicator',
  imports: [PalantirButtonComponent, PalantirIconComponent],
  template: `
    @if (delegationContext.activeDelegation(); as delegation) {
      <div class="delegation-float">
        <div class="delegation-float__badge">
          <palantir-icon name="partner-exchange-outline"></palantir-icon>
        </div>
        <div class="delegation-float__content">
          <span class="delegation-float__label">Cobrindo ausência de</span>
          <span class="delegation-float__name">{{ delegation.delegator.name }}</span>
        </div>
        <palantir-button
          size="2xs"
          theme="outline"
          variant="neutral"
          [attr.disabled]="deactivating() || undefined"
          (click)="deactivate()"
        >
          Encerrar
        </palantir-button>
      </div>
    }
  `,
  styles: `
    :host {
      position: fixed;
      bottom: 24px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 1000;
      pointer-events: none;
    }

    .delegation-float {
      pointer-events: auto;
      display: inline-flex;
      align-items: center;
      gap: 12px;
      padding: 8px 12px 8px 8px;
      border-radius: 100px;
      background: var(--palantir-color-neutrals-foreground-default, #fff);
      border: 1px solid var(--palantir-color-info-border-default, #bfdbfe);
      box-shadow: var(--palantir-shadow-elevation-3);
      animation: float-in 0.3s cubic-bezier(0.16, 1, 0.3, 1);
    }

    .delegation-float__badge {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: var(--palantir-color-info-surface-default, #eff6ff);
      color: var(--palantir-color-info-fill-default, #3b82f6);
      flex-shrink: 0;
    }

    .delegation-float__content {
      display: flex;
      flex-direction: column;
      line-height: 1.2;
    }

    .delegation-float__label {
      font-size: 11px;
      color: var(--palantir-color-neutrals-text-secondary, #6b7280);
    }

    .delegation-float__name {
      font-size: 13px;
      font-weight: 600;
      color: var(--palantir-color-neutrals-text-default, #111827);
      white-space: nowrap;
    }

    @keyframes float-in {
      from {
        opacity: 0;
        transform: translateY(12px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DelegationIndicatorComponent {
  protected readonly delegationContext = inject(DelegationContextService);
  private readonly delegationsService = inject(DelegationsService);
  private readonly authService = inject(AuthService);
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly deactivating = signal(false);

  protected readonly scopeLabel = computed(() => {
    const delegation = this.delegationContext.activeDelegation();
    if (!delegation) return '';
    return SCOPE_LABELS[delegation.scope] ?? delegation.scope;
  });

  constructor() {
    this.initDelegations();
  }

  private async initDelegations() {
    const isEnabled = await this.featureFlagService.isFeatureFlagEnabled(
      FeatureFlagsEnum.UserDelegations,
    );

    if (!isEnabled) return;

    this.delegationsService
      .getMyDelegations()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result.loading) return;
        const userId = this.authService.authenticatedUser()?.user?.id;
        if (userId) {
          this.delegationContext.syncActiveDelegations(
            result.delegations as DelegationFragment[],
            userId,
          );
        }
      });
  }

  deactivate() {
    const delegation = this.delegationContext.activeDelegation();
    if (!delegation) return;

    this.deactivating.set(true);
    this.delegationsService
      .deactivateDelegation({ id: delegation.id })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.deactivating.set(false),
        error: () => this.deactivating.set(false),
      });
  }
}
